import React, {Component, useEffect, useState} from "react";

import {EditOutlined} from "@ant-design/icons";
import {Button, Card, Descriptions, message} from "antd";
import {useForm} from "antd/es/form/Form";
import {Line} from "react-chartjs-2";

import api from "../../api";
import * as Auth from "../../AuthService";
import {checkAnyOfPermissions, getProjectId, labelMapper} from "../../helpers";
import {CustomTabs} from "../helpers/CustomTabs";
import {StandardForm} from "../helpers/forms/StandardForm";

import "./settings.css";

function GlobalSettings({projectId, constants}) {
	const [project, setProject] = useState({});
	const [globalSettings, setGlobalSettings] = useState({});
	const [values, setValues] = useState({});
	const [activeKey, setActiveKey] = useState("all");

	const [canEdit, setCanEdit] = useState(false);

	useEffect(() => {
		getProjectData();
		getGlobalValues();
		// getPossibleMVTypes()
		checkAnyOfPermissions(["insurance-admin", "admin", "material-analyst"]).then(() => setCanEdit(true));
	}, [projectId]);

	const getProjectData = () => {
		api.get(`/projects/${projectId}/info`).then(json => setProject(json.data));
	};
	const createSLAttributes = attributes =>
		attributes &&
		attributes.map(a => ({
			value: a["sl"],
			label: `Service Level: ${a.sl}%, CSF -  ${a.csf}`,
		}));

	const getGlobalValues = () => {
		api.get(`/projects/${projectId}/global-settings`)
			.then(json => {
				const _data = json.data.constants;
				for (const key of Object.keys(_data)) {
					if (key === "holding") {
						_data["holding"] = _data["holding"] * 100;
					} else if (key === "use_pm" && !_data["use_pm"]) {
						_data["use_pm"] = "corrective";
					}
				}
				setGlobalSettings(_data);
			})
			.catch(e => setGlobalSettings({}));
	};

	const getServiceFactorForm = (label, key, attributesList, attributesKey) => [
		{
			label: label,
			name: key,
			_type: "select-object",
			message: "Error",
			required: true,
			options: createSLAttributes(attributesList),
		},
	];

	const getStandardForm = (label, key, field_type, options) => {
		const field = {
			label: label,
			name: key,
			_type: field_type,
			message: "Error",
			required: true,
		};
		if (field_type === "select-object" && options) {
			field["options"] = options;
		}
		return [field];
	};

	const currency_settings = [
		{label: "Currency", key: "currency", editable: canEdit, field_type: "string"},
		{
			label: "Alternative Currency",
			key: "alt_currency",
			editable: canEdit,
			field_type: "string",
		},
		{label: "Currency Rate", key: "currency_rate", editable: canEdit, field_type: "string"},
	];

	const settings_span2 = [
		{
			label: "Service Factor Target (%)",
			key: "service_target",
			editable: canEdit,
			field_type: "string",
		},
		{
			label: "Holding Cost (%)",
			key: "holding",
			editable: canEdit,
			field_type: "number",
		},
		{
			label: "Order Cost",
			key: "order_cost",
			editable: canEdit,
			field_type: "number",
		},
		{
			label: "Average Stock Formula",
			key: "ave_stock_formula",
			editable: canEdit,
			field_type: "string",
		},
		{
			label: "Average Holding Cost Savings Target (%)",
			key: "ave_holding_savings",
			editable: canEdit,
			field_type: "string",
		},
		{
			label: "Demand forecast data",
			key: "use_pm",
			editable: canEdit,
			field_type: "select-object",
			required: true,
			options: [
				{
					value: "all",
					label: "All",
				},
				{
					value: "corrective",
					label: "Corrective",
				},
				{
					value: "planned",
					label: "Planned",
				},
			],
		},
		{
			label: "Use as main forecast (for recommendations ranking)",
			key: "forecast",
			options: [
				{
					value: "naive",
					label: labelMapper("naive"),
				},
				{
					value: "bom",
					label: labelMapper("bom"),
				},
				{
					value: "combined",
					label: labelMapper("combined"),
				},
			],
			editable: canEdit,
			field_type: "select-object",
		},
		{
			label: "Use as main recommendation (for BOM forecast)",
			key: "recommendation",
			options: [
				{
					value: "max_installed",
					label: "Maximum Quantity Installed per Unit",
				},
				{
					value: "total_installed",
					label: "Total Quantity Installed",
				},
			],
			editable: canEdit,
			field_type: "select-object",
		},
		{
			label: "Percentage (for BOM forecast)",
			key: "recommendation_percentage",
			editable: canEdit,
			field_type: "number",
		},
	];

	const settings_service_factor = [
		{
			label: "Service Level (Global)",
			key: "service_level_global",
			editable: canEdit,
		},
		{label: "Service Level (A item)", key: "service_level_a", editable: canEdit},
		{
			label: "Service Level (B item)",
			key: "service_level_b",
			editable: canEdit,
		},
		{label: "Service Level (C item)", key: "service_level_c", editable: canEdit},
	];

	const handleSave = value => {
		const _globalSettings = {...globalSettings, ...value};
		console.log(_globalSettings);
		for (const key of Object.keys(_globalSettings)) {
			if (key === "holding") {
				_globalSettings["holding"] = _globalSettings["holding"] / 100;
			} else if (key === "use_pm" && !_globalSettings["use_pm"]) {
				_globalSettings["use_pm"] = "corrective";
			}
		}
		api.post(`/projects/${projectId}/global-settings/save`, _globalSettings).then(() => {
			message.success("Global Settings were updated successfully!");
			getGlobalValues();
		});
	};

	const createStandardFormFields = list =>
		list.map(item => ({
			...item,
			formFields: item.editable ? getStandardForm(item.label, item.key, item.field_type, item.options) : null,
		}));

	const createServiceFactorFormFields = list =>
		list.map(item => ({
			...item,
			formFields: getServiceFactorForm(item.label, item.key, constants.service_factor_table, "sl"),
		}));

	const getSettingsTabs = () => [
		{
			name: "All Settings",
			key: "all",
			children: (
				<div style={{marginTop: 10}}>
					<CustomDescriptions
						span={3}
						handleSave={handleSave}
						values={globalSettings}
						data={createStandardFormFields(settings_span2)}
					/>
				</div>
			),
		},
		{
			name: "Currency Settings",
			key: "currency",
			children: (
				<div>
					<CustomDescriptions
						span={3}
						handleSave={handleSave}
						data={createStandardFormFields(currency_settings)}
						values={globalSettings}
					/>
				</div>
			),
		},
		{
			name: "Service Factor Settings",
			key: "service_factor",
			children: (
				<div>
					<ServiceFactorGraph constants={constants} />
					<CustomDescriptions
						span={3}
						data={createServiceFactorFormFields(settings_service_factor)}
						values={globalSettings}
						handleSave={handleSave}
					/>
				</div>
			),
		},
	];

	return (
		<>
			<div style={{fontSize: 20, marginLeft: "35%", marginBottom: 10}}>
				Project: <b>{project.project_name}</b> - Global Settings
			</div>
			<CustomTabs
				activeKey={activeKey}
				defaultKey={activeKey}
				tabsData={getSettingsTabs()}
				onChange={_activeKey => setActiveKey(_activeKey)}
			/>
		</>
	);
}

export function CustomDescriptions({data, values, span, handleSave, width = "30%"}) {
	const getMapperForBooleanValues = value => (value ? "Yes" : "No");

	const labels = {
		max_installed: "Maximum Quantity Installed per Unit",
		total_installed: "Total Quantity Installed",
	};

	const applyLabel = (field, value) => {
		if (field.key === "recommendation") {
			return labels[values[field.key]];
		}
		return value;
	};

	const getCells = field => {
		if (field.editable) {
			let value =
				typeof values[field.key] === "boolean"
					? getMapperForBooleanValues(values[field.key])
					: values[field.key];
			value = applyLabel(field, value);
			return (
				<EditableDescriptionCell
					handleSave={handleSave}
					value={value}
					field={field.key}
					formFields={field.formFields}
				/>
			);
		} else {
			return values[field.key] || "";
		}
	};

	return (
		<Card style={{marginTop: 10}} className="card-settings">
			<Descriptions size={"small"} bordered>
				{data.map((v, i) => (
					<Descriptions.Item key={i} labelStyle={{width: width}} span={span} label={v.label}>
						{getCells(v)}
					</Descriptions.Item>
				))}
			</Descriptions>
		</Card>
	);
}

function EditableDescriptionCell({value, field, formFields, handleSave}) {
	const [isEditMode, setIsEditMode] = useState(false);
	const [form] = useForm();

	const onFinish = values => {
		handleSave(values);
		form.resetFields([field]);
		setIsEditMode(false);
	};

	const onCancel = () => {
		form.resetFields([field]);
		setIsEditMode(false);
	};

	useEffect(() => {
		form.setFieldValue(field, value);
	}, [value]);

	const getValueLabel = () => {
		if (value) {
			if (typeof value === "object") {
				return value.join(", ");
			}
			return value;
		}
		return value;
	};

	return (
		<div>
			{isEditMode ? (
				<div>
					<StandardForm
						form={form}
						fields={formFields}
						initialValues={{[field]: value}}
						onFinish={onFinish}
					/>
					<div style={{float: "right"}}>
						<Button style={{marginRight: 10}} onClick={onCancel} type={"cancel"}>
							Cancel
						</Button>
						<Button onClick={() => form.submit()} className="adaptive-background-color" type={"submit"}>
							Save
						</Button>
					</div>
				</div>
			) : (
				<div>
					{getValueLabel()}
					<EditOutlined
						style={{float: "right"}}
						onClick={() => setIsEditMode(true)}
						className="adaptive-color"
					/>
				</div>
			)}
		</div>
	);
}

function ServiceFactorGraph({constants}) {
	const getChartData = () => {
		const table = constants.service_factor_table;

		return {
			labels: table.map(v => v["sl"]),
			datasets: [
				{
					label: "Customer Service Factor",
					data: table.map(v => v["csf"]),
					borderColor: "#00205c",
					backgroundColor: "rgba(0, 255, 128, 0)",
					fill: false,
				},
			],
		};
	};

	const options = {
		scales: {
			x: {
				type: "linear",
				min: 50,
				max: 105,
			},
		},
		interaction: {
			intersect: false,
			axis: "x",
		},
	};

	return (
		<>
			<Card className="card-settings">
				<Line height={75} data={getChartData()} options={options} />
			</Card>
		</>
	);
}

export class MinMaxSettings extends Component {
	constructor(props) {
		super(props);
		this.state = {
			project: {},
			project_id: getProjectId(this.props.location),
			settings: {
				constants: {
					service_factor_table: [],
					default_service_level: {},
				},
			},
		};
	}

	componentDidMount() {
		api.get(`/projects/${this.state.project_id}/info`, Auth.createConfig()).then(json =>
			this.setState({project: json.data}),
		);
		api.get(`/projects/${this.state.project_id}/settings`, Auth.createConfig()).then(json =>
			this.setState({settings: json.data}),
		);
	}

	render() {
		return (
			<Card className="card-settings" style={{height: "calc(100% - 10px)", margin: 5}}>
				<GlobalSettings projectId={this.state.project_id} constants={this.state.settings.constants} />
			</Card>
		);
	}
}
